<template>
  <div class="panel-body">
    <div class="container overflow-hidden">
      <div class="form-group">
        <text-input
          :errors="form.errors"
          label="Title"
          :required="true"
          :value="form.name"
          @input="updateForm('name', $event)"
        />
      </div>
      <div class="form-group mb-2">
        <div class="row">
          <div class="col-12 col-lg-8">
            <text-input
              :errors="form.errors"
              label="Street"
              placeholder="ex. Sesame street"
              :required="true"
              :value="form['company_street']"
              @input="updateForm('company_street', $event)"
            />
          </div>
          <div class="col-12 col-lg-4 pl-lg-0">
            <text-input
              :errors="form.errors"
              label="Street number"
              :min-width="150"
              placeholder="ex. 12/2"
              :required="true"
              :value="form['company_street_number']"
              @input="updateForm('company_street_number', $event)"
            />
          </div>
        </div>
      </div>
      <div class="form-group mb-2">
        <div class="row">
          <div class="col-12 col-lg-8">
            <text-input
              :errors="form.errors"
              label="City"
              placeholder="ex. Berlin"
              :required="true"
              :value="form['company_city']"
              @input="updateForm('company_city', $event)"
            />
          </div>
          <div class="col-12 col-lg-4 pl-lg-0">
            <text-input
              :errors="form.errors"
              label="Zip code"
              :min-width="150"
              placeholder="ex. 00-001"
              :required="true"
              :value="form['company_postal']"
              @input="updateForm('company_postal', $event)"
            />
          </div>
        </div>
      </div>
      <div class="form-group">
        <text-input
          :errors="form.errors"
          label="VAT Number"
          placeholder="ex. DE1112223344"
          :required="true"
          :value="form['company_vat']"
          @input="updateForm('company_vat', $event)"
        />
      </div>
      <div class="form-group">
        <label
          class="input-label"
          for="company_country"
        >
          Country
          <span class="required">*</span>
        </label>
        <div class="d-flex flex-row">
          <input-dropdown
            id="company_country"
            v-model:value="companyCountry"
            :allow-empty="false"
            :close-on-select="true"
            :max-width="460"
            :min-width="460"
            :multiple="false"
            name="company_country"
            open-direction="top"
            :options="sortedCountries"
            placeholder="Search for country"
            :preselect-first="false"
            track-by="code"
            track-label="name"
          >
            <template #single-option="{ option }">
              {{ option.name }}
            </template>
            <template #option-row="{ option }">
              {{ option.name }}
            </template>
          </input-dropdown>
        </div>
      </div>
      <div class="form-group">
        <label
          class="input-label"
          for="pricing_package"
        >Pricing package</label>
        <input-dropdown
          id="pricing_package"
          v-model:value="pricingPackage"
          :max-width="460"
          :min-width="460"
          :allow-empty="false"
          name="pricing_package"
          open-direction="top"
          :options="pricingPackages"
          :class="{ 'is-invalid': form.errors.has('pricing_package') }"
          placeholder="Search for package"
          :show-labels="false"
          track-by="id"
          track-label="name"
          @input="selectPackage"
        >
          <template #single-option="{ option }">
            {{ option.name }}
          </template>
          <template #option-row="{ option }">
            {{ option.name }}
          </template>
        </input-dropdown>
        <div
          v-if="form.errors.has('owner_id')"
          class="invalid-feedback"
          v-text="form.errors.get('owner_id')"
        />
      </div>
      <div v-if="form.pricing_package">
        <div class="form-group mt-2">
          <text-input
            label="Target impressions"
            :max="9999999999999998"
            :min="0"
            placeholder="Enter your impressions"
            :separators="true"
            type="number"
            :value="form.impression_target * 1000"
            @input="updateForm('impression_target', parseInt($event) / 1000)"
          />
        </div>
      </div>
      <div v-if="form.pricing_package">
        <div class="row mb-2">
          <div class="col-12 col-lg-12">
            <text-input
              id="cpm_price"
              :errors="form.errors"
              label="CPM price"
              :min-width="210"
              :required="true"
              :step="0.01"
              type="number"
              :value="form.cpm_price"
              @input="updateForm('cpm_price', $event)"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import countries from "../../countries"
import InputDropdown from "@/components/common/dropdown/InputDropdown.vue"
import Utils from "@/utils"

export default {
  name: "CompanyForm",
  components: {
    InputDropdown
  },
  props: {
    form: {
      type: [Object],
      default: () => { return {} }
    }
  },
  emits: ["clear-form-field", "update-form"],
  data () {
    return {
      pricingPackages: [
        {
          id: "beginner",
          name: "Beginner",
          data: {
            cpm_price: 0.90,
            impression_target: 1200
          }
        },
        {
          id: "standard",
          name: "Standard",
          data: {
            cpm_price: 0.70,
            impression_target: 7500
          }
        },
        {
          id: "pro",
          name: "Pro",
          data: {
            cpm_price: 0.60,
            impression_target: 17500
          }
        },
        {
          id: "custom",
          name: "Custom",
          data: {
            cpm_price: 0,
            impression_target: 0
          }
        }
      ]
    }
  },
  computed: {
    sortedCountries () {
      const countries = Utils.cloneDeep(this.countries)
      return countries.sort((a, b)=>{
        if (a.name < b.name) {
          return -1
        }
        if (a.name > b.name) {
          return 1
        }
        return 0
      })
    },
    countries () {
      return countries
    },
    pricingPackage: {
      get () {
        return this.pricingPackages.find((row) => row.id === this.form.pricing_package)
      },
      set (pricingPackage) {
        this.updateForm("pricing_package", pricingPackage.id)
      }
    },
    companyCountry: {
      get () {
        return this.countries.find((row) => row.code === this.form.company_country)
      },
      set (value) {
        this.updateForm("company_country", value.code)
      }
    }
  },
  methods: {
    updateForm (property, value) {
      this.$emit("update-form", { property, value })
    },
    selectPackage (row) {
      const pack = this.pricingPackages.find((pack) => pack.id === row.id)
      for (const key in pack.data) {
        if (pack.data.hasOwnProperty(key)) {
          this.updateForm(key, pack.data[key])
        }
      }
      this.form.errors.clear("pricing_package")
    }
  }
}
</script>
